<template>
  <UContainer @click.right.prevent @copy.prevent @paste.prevent>
    <InterviewHeader :count-questions="countQuestions" :language="categoryTitle" />
    <UCard>
      <div
        v-if="!userStore.isFptMarket"
        class="flex gap-3 mb-3 p-1 bg-neutral-50 dark:bg-neutral-800 rounded-lg flex-col md:flex-row"
      >
        <UButton
          v-for="topic in topics"
          :key="topic.slug"
          :label="topic.label"
          :color="topicSlugSelected === topic.slug ? 'secondary' : 'neutral'"
          variant="soft"
          block
          :to="topic.url"
          :icon="topic.icon"
          @click="topicSlugSelected = topic.slug"
        />
      </div>
      <InterviewCategory
        v-if="(categories?.length || 0) > 0"
        :categories="categories"
        :topic-selected="topicSlugSelected"
        :category-selected="categorySlugSelected"
      />
      <b v-else>{{ LL.no_results() }}</b>
    </UCard>

    <div class="mt-8">
      <InterviewQuestionHeader
        v-if="categorySlugSelected"
        :category-title="categoryTitle"
        :question-count="questions.length"
      />
      <div v-if="isLoading" class="mt-15">
        <div class="flex flex-col gap-3">
          <UCard v-for="i in 10" :key="i" class="mb-3">
            <div class="flex justify-between">
              <div class="flex flex-col gap-5">
                <USkeleton :class="`h-8 w-72`" />
                <USkeleton class="h-4 w-32" />
              </div>
              <div class="flex gap-3 items-center">
                <USkeleton class="h-4 w-4" />
                <USkeleton class="h-8 w-16" />
              </div>
            </div>
          </UCard>
        </div>
      </div>
      <InterviewQuestions
        :questions="questions"
        @open-explain-rank="handleOpenExplainRank"
      />
      <LazyEmpty
        v-if="!isLoading && questions.length === 0"
        :description="LL.no_results()"
      />
      <UModal v-model:open="isOpenExplainRank">
        <template #content>
          <UCard>
            <div class="flex items-end">
              <UIcon
                class="cursor-pointer ml-auto font-semibold text-xl"
                name="i-heroicons-x-mark"
                @click="isOpenExplainRank = false"
              />
            </div>
            <h3 class="text-3xl font-bold text-center capitalize">
              {{ explainRank }} Level
            </h3>
            <div class="flex flex-col items-center">
              <ImagePreset
                :src="`/images/rank/${explainRank}.svg`"
                :alt="explainRank"
                width="295"
                height="263"
                :preload="true"
              />
              <UCard class="mb-3">
                <p class="text-lg text-center text-neutral-600">
                  {{ explainContent }}
                </p>
              </UCard>
              <UAlert
                icon="i-heroicons-information-circle"
                color="warning"
                variant="subtle"
                description="Từ câu hỏi middle, bạn cần nâng cấp tài khoản để mở khoá"
              />
            </div>
          </UCard>
        </template>
      </UModal>
    </div>
  </UContainer>
</template>

<script lang="ts" setup>
import { removeEmptyObj } from "~/utils";
import { useUserStore } from "../../../store/user";
import { getCategories } from "features/categories";
import {
  TQuestion,
  TQuestionsParams,
  getCountQuestions,
  getQuestions,
} from "features/question";
import { defaultSeoOgImage } from "features/seo";
import { typesafeI18n } from "~/locales/i18n-vue";
import Ranks from "~/constants/ranks";
import { TGetRandomUserPremiumResponse } from "~/features/user";

definePageMeta({
  layout: "page",
});

const route = useRoute();
const { LL } = typesafeI18n();
const { postfixMarket } = usePostFixMarket();

const questions = ref<TQuestion[]>([]);
const categoryTitle = ref<string>("");
const isLoading = ref(true);
const userStore = useUserStore();
const explainRank = ref(Ranks.Junior);
const isOpenExplainRank = ref(false);
const toast = useToast();
const config = useRuntimeConfig();

const ranks = computed(() => (route.query.ranks as string) || "");
const keyword = computed(() => (route.query.keyword as string) || "");
const topicSlugSelected = ref(String(route.params.topic) || "full-stack-web-and-mobile");
const categorySlugSelected = computed(() => String(route.params.category) || "java");
const topics = ref([
  {
    label: "Full-stack, Web & Mobile",
    slug: "full-stack-web-and-mobile",
    url: `/phong-van-pro/full-stack-web-and-mobile${postfixMarket}/java${postfixMarket}`,
    icon: "i-heroicons-code-bracket",
  },
  {
    label: "Coding & Data structures",
    slug: "coding-and-data-structures",
    url: `/phong-van-pro/coding-and-data-structures${postfixMarket}/data-structures${postfixMarket}`,
    icon: "i-heroicons-cube-transparent",
  },
  {
    label: "System design & Architecture",
    slug: "system-design-and-architecture",
    url: `/phong-van-pro/system-design-and-architecture${postfixMarket}/docker${postfixMarket}`,
    icon: "i-heroicons-squares-2x2",
  },
]);

const handleOpenExplainRank = (rank: string) => {
  explainRank.value = rank;
  isOpenExplainRank.value = true;
};

const explainContent = computed(() => {
  if (explainRank.value === Ranks.Entry) {
    return LL.value.entry_explain();
  } else if (explainRank.value === Ranks.Junior) {
    return LL.value.junior_explain();
  } else if (explainRank.value === Ranks.Middle) {
    return LL.value.middle_explain();
  } else if (explainRank.value === Ranks.Senior) {
    return LL.value.senior_explain();
  } else if (explainRank.value === Ranks.Expert) {
    return LL.value.expert_explain();
  } else {
    return Ranks.Entry;
  }
});

const questQuery: ComputedRef<TQuestionsParams> = computed(() =>
  removeEmptyObj({
    categorySlug: categorySlugSelected.value,
    rankSlug: ranks.value,
    keyword: keyword.value,
  })
);

const [
  { data: categories },
  questionsResponse,
  { data: countQuestions },
] = await Promise.all([
  getCategories({ topic: topicSlugSelected.value }),
  getQuestions(questQuery.value),
  getCountQuestions(),
]);

questions.value = questionsResponse?.items || [];
isLoading.value = false;
categoryTitle.value =
  categories?.value?.find((c) => c.slug === categorySlugSelected.value)?.title ?? "";

onMounted(() => {
  try {
    if (!userStore.isUpgraded)
      setTimeout(async () => {
        const result: TGetRandomUserPremiumResponse = await $fetch(
          `${config.public.apiBase}/api/users/random-user-roles`
        );
        const user = result.data;
        toast.add({
          id: "congratulation",
          title: `${LL.value.congratulations()} ${user.firstName} ${user.lastName}`,
          description: `${LL.value.upgrade_success()} ${user.roles?.[0]?.name ?? "GOLD"}`,
          avatar: {
            src: `${user.imageUrl}`,
            chipColor: "primary",
            text: "",
            chipPosition: "top-right",
            alt: `${user.firstName} ${user.lastName}`,
          },
          duration: 8000,
          actions: [
            {
              icon: "i-heroicons-rocket-launch",
              label: LL.value.upgrade_account(),
              to: "/nang-cap-tai-khoan",
            },
          ],
        });
      }, 10000);
  } catch (error) {
    console.log(error);
  }
});
watch(
  () => route.query.ranks as string,
  (_) => {
    refreshData();
  }
);
watch(
  () => route.query.keyword as string,
  (_) => {
    refreshData();
  }
);

async function refreshData() {
  isLoading.value = true;
  const data = await getQuestions(questQuery.value);
  isLoading.value = false;
  if (data) {
    questions.value = data.items;
  }
}

const title = computed(() => {
  if (categorySlugSelected.value.includes("global"))
    return `Interview questions for ${categoryTitle.value} developers`;
  return `Bộ câu hỏi phỏng vấn ${categoryTitle.value} dành cho lập trình viên`;
});

const description = computed(() => {
  const count = questionsResponse.items?.length ?? 0;
  const category = categorySlugSelected.value;
  if (categorySlugSelected.value.includes("global"))
    return `Top ${count} ${category} interview questions for developers, from fresher to senior. Top ${count} ${category} interview questions.`;
  return `Tổng hợp ${count} câu hỏi phỏng vấn ${category} từ fresher đến senior dành cho lập trình viên. Top ${count} câu hỏi phỏng vấn ${category}`;
});

useSeoMeta({
  title: title.value,
  description: description.value,
  ...defaultSeoOgImage,
});
</script>
